import { useState, useEffect } from "react";
import { format, parseISO } from "date-fns";
import { Loader2, ChevronDown, ChevronUp, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useReportResponses } from "@/hooks/useReportResponses";
import { QuestionFormat } from "@/types/generated";

// Helper function to safely parse JSON data from GraphQL
function safelyParseData(data: any): any {
  if (data === null || data === undefined) {
    return null;
  }
  
  // If it's already an array, return as is
  if (Array.isArray(data)) {
    return data;
  }
  
  // If it's an object but not an array and not null, return as is
  if (typeof data === 'object' && data !== null) {
    return data;
  }
  
  // If it's a string, try to parse it
  if (typeof data === 'string') {
    try {
      // String format from GraphQL looks like: "[\"item1\",\"item2\"]"
      // First attempt: Try to parse the raw JSON string
      try {
        const parsed = JSON.parse(data);
        return parsed;
      } catch (e) {
        // If that fails, continue with other parsing strategies
      }
      
      // Second attempt: For strings that look like arrays or objects
      if ((data.startsWith('[') && data.endsWith(']')) || 
          (data.startsWith('{') && data.endsWith('}'))) {
        // Some GraphQL implementations return JSON as strings that need double parsing
        // For example, stringified JSON: "[\"item1\",\"item2\"]"
        try {
          // Try a different approach for escaped sequences
          const normalizedStr = data.replace(/\\"/g, '"');
          return JSON.parse(normalizedStr);
        } catch (e) {
          // Continue to next attempt
        }
      }
      
      // Third attempt: Handle double-encoded strings
      if (data.startsWith('"') && data.endsWith('"')) {
        // Try to unwrap the outer quotes and parse
        try {
          // First remove the outer quotes
          const unwrapped = data.substring(1, data.length - 1);
          
          // Then handle escaped sequences properly
          const unescaped = unwrapped
            .replace(/\\"/g, '"')
            .replace(/\\\\/g, '\\')
            .replace(/\\n/g, '\n');
          
          // If the unwrapped content looks like JSON, parse it
          if ((unescaped.startsWith('{') && unescaped.endsWith('}')) || 
              (unescaped.startsWith('[') && unescaped.endsWith(']'))) {
            return JSON.parse(unescaped);
          }
          
          // Otherwise return the unescaped string
          return unescaped;
        } catch (e) {
          // Fallback to returning the unwrapped string
          return data.substring(1, data.length - 1);
        }
      }
      
      // Just return the string if we can't parse it
      return data;
    } catch (e) {
      return data;
    }
  }
  
  // For other primitive values
  return data;
}

// Add a debugging component for development
function DebugData({ data, label }: { data: any, label: string }) {
  if (typeof window === 'undefined' || window.location.hostname !== 'localhost') {
    return null;
  }
  
  return (
    <details className="mt-2 text-xs border border-gray-200 rounded p-2">
      <summary className="font-mono cursor-pointer">{label}</summary>
      <pre className="mt-2 p-2 bg-gray-50 overflow-auto max-h-[200px]">
        {typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data)}
      </pre>
    </details>
  );
}

interface IndividualUpdatesProps {
  reportId: string;
}

export function IndividualUpdates({ reportId }: IndividualUpdatesProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEmployees, setExpandedEmployees] = useState<string[]>([]);
  
  // Use the GraphQL hook to fetch responses
  const {
    loading,
    error,
    responses,
    hasMore,
    loadMore
  } = useReportResponses(reportId);

  // Set all employees to expanded by default when responses are loaded
  useEffect(() => {
    if (responses.length > 0) {
      // We only want to set this once when responses first load, not on every change
      // Only set expanded employees if the array is empty to avoid infinite re-renders
      if (expandedEmployees.length === 0) {
        const employeeIds = responses.map(response => response.employee.id);
        setExpandedEmployees(employeeIds);
      }
    }
  }, [responses, expandedEmployees.length]); // Add expandedEmployees.length to dependency array

  // Filter responses by search term
  const filteredResponses = responses.filter(response => 
    response.employee.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle accordion toggle
  const toggleEmployee = (employeeId: string) => {
    setExpandedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  if (loading && responses.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-sm text-muted-foreground">Loading updates...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Error loading updates</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{error.message}</p>
          <Button 
            className="mt-4" 
            variant="outline" 
            onClick={loadMore}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (responses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No updates available</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No employee updates were found for this report date.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Debug Info (development only) */}
      {typeof window !== 'undefined' && window.location.hostname === 'localhost' && (
        <DebugData data={responses} label="All Responses" />
      )}
      
      {/* Search Filter */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Employee Responses */}
      <div className="space-y-4">
        {filteredResponses.length === 0 ? (
          <Card>
            <CardContent className="py-6">
              <p className="text-center text-muted-foreground">No employees found matching "{searchTerm}"</p>
            </CardContent>
          </Card>
        ) : (
          filteredResponses.map((response) => (
            <EmployeeResponseCard
              key={response.id}
              response={{
                id: response.id,
                submittedAt: response.submittedAt,
                employee: response.employee,
                answers: response.answers.map(answer => ({
                  id: answer.id,
                  questionId: answer.questionId,
                  answerText: answer.answerText,
                  extractedData: answer.extractedData,
                  question: {
                    id: answer.question.id,
                    header: answer.question.header,
                    format: answer.question.format as QuestionFormat,
                    questions: answer.question.questions,
                    responseSchema: answer.question.responseSchema
                  }
                }))
              }}
              isExpanded={expandedEmployees.includes(response.employee.id)}
              onToggle={() => toggleEmployee(response.employee.id)}
            />
          ))
        )}
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center mt-4">
          <Button 
            variant="outline" 
            onClick={loadMore} 
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

interface EmployeeResponseCardProps {
  response: {
    id: string;
    submittedAt: string;
    employee: {
      id: string;
      name: string;
      avatarUrl?: string | null;
    };
    answers: Array<{
      id: string;
      questionId: string;
      answerText: any;
      extractedData?: any;
      question: {
        id: string;
        header: string;
        format: QuestionFormat;
        questions: any;
        responseSchema: any;
      };
    }>;
  };
  isExpanded: boolean;
  onToggle: () => void;
}

function EmployeeResponseCard({ response, isExpanded, onToggle }: EmployeeResponseCardProps) {
  const { employee, submittedAt, answers } = response;
  const hasResponse = !!submittedAt;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card className={cn(
      "transition-all duration-200",
      !hasResponse && "border-dashed opacity-70"
    )}>
      <CardHeader 
        className={cn(
          "flex flex-row items-center justify-between cursor-pointer",
          isExpanded && "pb-2"
        )}
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={employee.avatarUrl || undefined} />
            <AvatarFallback>{getInitials(employee.name)}</AvatarFallback>
          </Avatar>
          <div>
            <CardTitle className="text-base">{employee.name}</CardTitle>
          </div>
          
          {hasResponse ? (
            <Badge 
              variant="outline" 
              className="ml-3 text-xs font-normal bg-primary/5"
            >
              {format(parseISO(submittedAt), "h:mm a")}
            </Badge>
          ) : (
            <Badge 
              variant="outline" 
              className="ml-3 text-xs font-normal bg-muted/30 text-muted-foreground"
            >
              No update
            </Badge>
          )}
        </div>
        
        <div>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          {typeof window !== 'undefined' && window.location.hostname === 'localhost' && (
            <DebugData data={answers} label="Raw Answers" />
          )}
          
          {!hasResponse ? (
            <p className="text-sm text-muted-foreground italic py-2">
              No update was submitted for this report.
            </p>
          ) : answers.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-2">
              Update was submitted but contains no answers.
            </p>
          ) : (
            <div className="space-y-4 pt-2">
              {answers.map(answer => (
                <QuestionAnswer 
                  key={answer.id}
                  answer={answer}
                />
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

interface QuestionAnswerProps {
  answer: {
    id: string;
    questionId: string;
    answerText: any;
    extractedData?: any;
    question: {
      id: string;
      header: string;
      format: QuestionFormat;
      questions: any;
      responseSchema: any;
    };
  };
}

function QuestionAnswer({ answer }: QuestionAnswerProps) {
  const { question, extractedData } = answer;

  // Remove debug logs that are causing console flooding
  
  // Render nothing if there's no extracted data
  if (extractedData === null || extractedData === undefined) {
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium">{question.header}</h4>
        <p className="text-sm text-muted-foreground italic">Not answered</p>
        {typeof window !== 'undefined' && window.location.hostname === 'localhost' && (
          <DebugData data={extractedData} label="Extracted Data" />
        )}
      </div>
    );
  }

  // Handle different question formats
  switch (question.format) {
    case QuestionFormat.ARRAY:
      // Use extractedData for arrays
      const arrayItems = extractedData;
      return (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{question.header}</h4>
          {typeof window !== 'undefined' && window.location.hostname === 'localhost' && (
            <DebugData data={{ extracted: extractedData }} label="Array Debug" />
          )}
          {Array.isArray(arrayItems) && arrayItems.length > 0 ? (
            <div className="space-y-1">
              {arrayItems.map((item, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-sm mr-2">•</span>
                  <p className="text-sm">{typeof item === 'object' ? JSON.stringify(item) : item}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No items</p>
          )}
        </div>
      );
    case QuestionFormat.OBJECT:
      // Use extractedData for objects
      const objectData = extractedData;
      return (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{question.header}</h4>
          {typeof window !== 'undefined' && window.location.hostname === 'localhost' && (
            <DebugData data={{ extracted: extractedData }} label="Object Debug" />
          )}
          {objectData && typeof objectData === 'object' ? (
            <ObjectResponseRenderer 
              data={objectData}
              schema={question.responseSchema}
            />
          ) : (
            <p className="text-sm text-muted-foreground italic">No data</p>
          )}
        </div>
      );
    case QuestionFormat.STRING:
    default:
      return (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{question.header}</h4>
          {typeof window !== 'undefined' && window.location.hostname === 'localhost' && (
            <DebugData data={{ extracted: extractedData }} label="String Debug" />
          )}
          {typeof extractedData === 'string' && extractedData.trim() ? (
            <p className="text-sm">{extractedData}</p>
          ) : typeof extractedData === 'object' ? (
            <p className="text-sm">{JSON.stringify(extractedData)}</p>
          ) : (
            <p className="text-sm text-muted-foreground italic">No response</p>
          )}
        </div>
      );
  }
}

interface ObjectResponseRendererProps {
  data: any;
  schema: any;
}

function ObjectResponseRenderer({ data: rawData }: ObjectResponseRendererProps) {
  // Parse the data which might be a string representing JSON
  const data = safelyParseData(rawData);
  
  // If data is not an object, display it directly
  if (typeof data !== 'object' || data === null) {
    return (
      <p className="text-sm">
        {typeof data === 'string' ? data : JSON.stringify(data)}
      </p>
    );
  }

  // Special case for blockers object from the standup template
  if ('has_blocker' in data && 'blockers' in data) {
    if (!data.has_blocker) {
      return (
        <p className="text-sm">No blockers reported</p>
      );
    }

    if (Array.isArray(data.blockers) && data.blockers.length > 0) {
      return (
        <div className="space-y-3">
          {data.blockers.map((blocker: any, index: number) => (
            <div key={index} className="border rounded-md p-3 bg-muted/10">
              <h5 className="text-sm font-medium">{blocker.blocker_name || 'Unnamed blocker'}</h5>
              {blocker.blocker_description && (
                <p className="text-sm mt-2">{typeof blocker.blocker_description === 'object' ? 
                  JSON.stringify(blocker.blocker_description) : blocker.blocker_description}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {blocker.blocker_help_needed && (
                  <Badge variant="outline" className="text-xs">
                    Help needed: {typeof blocker.blocker_help_needed === 'object' ? 
                      JSON.stringify(blocker.blocker_help_needed) : blocker.blocker_help_needed}
                  </Badge>
                )}
                <Badge 
                  variant={blocker.blocker_discuss_today ? "default" : "outline"}
                  className="text-xs"
                >
                  {blocker.blocker_discuss_today ? "Discuss today" : "No discussion needed"}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <p className="text-sm text-muted-foreground italic">
        Blockers reported but none specified
      </p>
    );
  }

  // Generic object renderer for other object types
  return (
    <div className="text-sm space-y-2">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="mb-1">
          <span className="font-medium">{key}: </span>
          <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
        </div>
      ))}
    </div>
  );
} 